import React from "react";
import { Badge, Card, Group, Stack, Text, ThemeIcon } from "@mantine/core";
import {
  IconCheck,
  IconClipboardList,
  IconFileText,
  IconSettings,
} from "@tabler/icons-react";
import {
  FormCategory,
  FormTemplate,
  FormTemplateSource,
} from "../../types/formTemplates";
import styles from "./TemplateCard.module.css";

interface TemplateCardProps {
  template: FormTemplate;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

function categoryIcon(category: FormCategory, source: FormTemplateSource) {
  if (source === "custom") return IconSettings;
  if (category === "per_visit") return IconFileText;
  if (category === "per_visit_clinical") return IconClipboardList;
  return IconFileText;
}

function formatUpdated(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  selected = false,
  disabled = false,
  onClick,
}) => {
  const Icon = categoryIcon(template.category, template.source);
  const isCustom = template.source === "custom";

  const cardClassName = [
    styles.card,
    selected ? styles.cardSelected : "",
    disabled ? styles.cardDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      onClick={disabled ? undefined : onClick}
      className={cardClassName}
      aria-disabled={disabled}
      aria-selected={selected}
      role="button"
    >
      {selected && (
        <div className={styles.checkBadge}>
          <IconCheck size={14} />
        </div>
      )}

      <Group align="flex-start" gap="sm" wrap="nowrap" mb="xs">
        <ThemeIcon
          size={40}
          radius="md"
          variant="default"
          color={isCustom ? "burntOrange" : "teal"}
          className={styles.iconTile}
        >
          <Icon
            size={22}
            color={
              isCustom
                ? "var(--brand-burnt-orange)"
                : "var(--mantine-color-teal-6)"
            }
          />
        </ThemeIcon>
        <Stack
          gap={2}
          style={{ flex: 1, minWidth: 0 }}
          className={styles.contentStack}
        >
          <Group gap="xs" wrap="nowrap" align="center">
            <Text fw={600} size="sm" truncate className={styles.titleText}>
              {template.name}
            </Text>
            <Badge
              size="xs"
              radius="sm"
              variant="default"
              color="gray"
              className={`${styles.versionBadge} ${styles.metaBadge}`}
            >
              v{template.version}
            </Badge>
            {template.is_mock_placeholder && (
              <Badge
                size="xs"
                radius="sm"
                variant="default"
                color="gray"
                className={styles.metaBadge}
              >
                Coming soon
              </Badge>
            )}
          </Group>
          {template.description && (
            <Text c="dimmed" size="xs" lineClamp={2}>
              {template.description}
            </Text>
          )}
        </Stack>
      </Group>

      <Group gap="xs" mt="sm" pt="sm" className={styles.bottomRow}>
        <Badge
          variant="default"
          color="gray"
          size="xs"
          radius="sm"
          className={styles.metaBadge}
        >
          {template.field_count}{" "}
          {template.field_count === 1 ? "Field" : "Fields"}
        </Badge>
        <Badge
          variant="default"
          color="gray"
          size="xs"
          radius="sm"
          className={styles.metaBadge}
        >
          Updated {formatUpdated(template.updated_dt)}
        </Badge>
      </Group>
    </Card>
  );
};
